import { useMemo, useState } from 'react';
import { Icoon } from '../ui/Icoon';
import { Link, useParams } from 'react-router-dom';
import { agileUren, agileVoortgang, ITEM_STATUS_LABEL, fmtDag, fmtUren, geplandeUren, takenpakketVoortgang, werkpakketVoortgang, type PakketStatus } from '@ep/core';
import { api } from '../lib/api';
import { NIVEAUS, useActie, useData, useMag, useModule, useTaakOpenen, type TakenpakketRij, type WerkpakketRij } from '../lib/data';
import { TakenTabel } from '../ui/TakenTabel';
import { EigenVeldenInvoer } from '../ui/EigenVelden';
import { Badge, Balk, getal, Laden, Modal, Veld, VerwijderKnop } from '../ui/ui';

const ITEM_TOON = { BACKLOG: 'grijs', GEPLAND: 'blauw', BEZIG: 'oranje', REVIEW: 'accent', KLAAR: 'groen' } as const;
const TOON: Record<PakketStatus, 'accent' | 'oranje' | 'blauw' | 'groen' | 'rood'> = { Open: 'accent', Wacht: 'oranje', Bezig: 'blauw', Ingepland: 'groen', 'Te laat': 'rood' };

export function Werkpakketten() {
  const taak = useTaakOpenen();
  const agileAan = useModule('AGILE');
  const { id } = useParams();
  const { data: d } = useData();
  const mag = useMag();
  const [sel, setSel] = useState<string | 'los'>('los');
  const [wpForm, setWpForm] = useState<WerkpakketRij | 'nieuw' | null>(null);
  const [tpForm, setTpForm] = useState<TakenpakketRij | 'nieuw' | null>(null);
  const [tplOpen, setTplOpen] = useState(false);
  const [expOpen, setExpOpen] = useState(false);
  const p = d?.projecten.find((x) => x.id === id);
  const wps = useMemo(() => (d ? d.werkpakketten.filter((w) => w.projectId === id).sort((a, b) => a.volgorde - b.volgorde) : []), [d, id]);
  const tps = useMemo(() => (d ? d.takenpakketten.filter((t) => t.projectId === id) : []), [d, id]);
  const taken = useMemo(() => (d ? d.taken.filter((t) => t.projectId === id) : []), [d, id]);
  const volgorde = useActie((ids: string[]) => api.post('/api/werkpakketten/volgorde', { ids }));
  const verwijderTp = useActie((tid: string) => api.del(`/api/takenpakketten/${tid}`), { succes: 'Takenpakket verwijderd' });

  if (!d) return <Laden />;
  if (!p) return <div className="pagina paneel">Project niet gevonden</div>;
  const geselecteerd = sel === 'los' ? null : wps.find((w) => w.id === sel) ?? null;
  const agile = geselecteerd?.werkwijze === 'AGILE';
  const tpsHier = tps.filter((t) => (geselecteerd ? t.werkpakketId === geselecteerd.id : !t.werkpakketId)).sort((a, b) => a.volgorde - b.volgorde);
  const losseTaken = taken.filter((t) => !t.takenpakketId && (geselecteerd ? t.werkpakketId === geselecteerd.id : !t.werkpakketId));
  const budget = p.urenBudget ?? p.expertiseBudget.reduce((s, b) => s + b.uren, 0);
  const inPakketten = tps.reduce((s, t) => s + t.uren, 0);
  const verschuif = (i: number, r: number) => {
    const ids = wps.map((w) => w.id);
    [ids[i], ids[i + r]] = [ids[i + r], ids[i]];
    volgorde.mutate(ids);
  };

  return (
    <div className="pagina kolom" style={{ gap: 16 }}>
      <div className="rij wrap">
        <Link to={`/projecten/${p.id}`} className="knop">
          ← {p.nummer}
        </Link>
        <h1 className="groei">Werkzaamheden · {p.naam}</h1>
        <button className="knop" onClick={() => setExpOpen(true)}>Uren per expertise</button>
        {mag('pakketten.schrijven') && <button className="knop" onClick={() => setTplOpen(true)}>Uit template</button>}
      </div>
      <div className="kaartjes">
        <div className="kaartje"><div className="getal">{wps.length}</div><div className="lbl">werkpakketten</div></div>
        <div className="kaartje"><div className="getal">{tps.length}</div><div className="lbl">takenpakketten</div></div>
        <div className="kaartje"><div className="getal">{fmtUren(inPakketten)} / {fmtUren(budget)}</div><div className="lbl">uren in pakketten / projectbudget</div></div>
        <div className="kaartje"><div className="getal">{fmtUren(geplandeUren(taken) + agileUren(tps).ingepland)}</div><div className="lbl">uren ingepland (agile: geschreven)</div></div>
        <div className="kaartje"><div className="getal">{fmtDag(p.livegang, 'jaar') || '–'}</div><div className="lbl">livegang</div></div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 540px) 1fr', gap: 16, alignItems: 'start' }}>
        <div className="paneel kolom">
          <div className="rij">
            <h2 className="groei">Werkpakketten</h2>
            {mag('pakketten.schrijven') && <button className="knop primair klein" onClick={() => setWpForm('nieuw')}>+ Werkpakket</button>}
          </div>
          {wps.map((w, i) => {
            const v = werkpakketVoortgang(w, wps, tps, taken, d.vandaag);
            const na = wps.find((x) => x.id === w.afhankelijkVanId);
            return (
              <div key={w.id} className="paneel vlak klik" style={{ cursor: 'pointer', borderColor: sel === w.id ? 'var(--accent)' : undefined, background: sel === w.id ? 'var(--accent-bg)' : undefined }} onClick={() => setSel(w.id)}>
                <div className="rij">
                  <b className="groei">{i + 1}. {w.naam}</b>
                  {w.werkwijze === 'AGILE' && <Badge toon="blauw">Agile</Badge>}
                  <Badge toon={TOON[v.status]}>{v.label}</Badge>
                </div>
                <div className="klein mute">
                  {w.deadline ? `deadline ${fmtDag(w.deadline, 'jaar')}` : 'geen deadline'}
                  {na ? ` · ↪ na ${na.naam}` : ''} ·{' '}
                  {w.werkwijze === 'AGILE'
                    ? (() => { const a = agileVoortgang(tps.filter((t) => t.werkpakketId === w.id).map((t) => ({ uren: t.uren, status: t.status ?? 'BACKLOG' }))); return `${fmtUren(a.klaar)} / ${fmtUren(a.totaal)}u klaar`; })()
                    : `${fmtUren(v.ingepland)} / ${fmtUren(v.uren)}u ingepland`}
                </div>
                {w.werkwijze === 'AGILE'
                  ? (() => { const a = agileVoortgang(tps.filter((t) => t.werkpakketId === w.id).map((t) => ({ uren: t.uren, status: t.status ?? 'BACKLOG' }))); return <Balk waarde={a.klaar} max={a.totaal} />; })()
                  : <Balk waarde={v.ingepland} max={v.uren} />}
                {mag('pakketten.schrijven') && (
                  <div className="rij" style={{ marginTop: 6 }} onClick={(e) => e.stopPropagation()}>
                    <button className="icoon-knop" disabled={i === 0} onClick={() => verschuif(i, -1)} title="Omhoog">˄</button>
                    <button className="icoon-knop" disabled={i === wps.length - 1} onClick={() => verschuif(i, 1)} title="Omlaag">˅</button>
                    <button className="icoon-knop" onClick={() => setWpForm(w)} title="Bewerken"><Icoon naam="pencil" maat={16} /></button>
                  </div>
                )}
              </div>
            );
          })}
          <div className="paneel vlak" style={{ cursor: 'pointer', borderColor: sel === 'los' ? 'var(--accent)' : undefined, background: sel === 'los' ? 'var(--accent-bg)' : undefined }} onClick={() => setSel('los')}>
            <b>Direct onder het project</b>
            <div className="klein mute">{tps.filter((t) => !t.werkpakketId).length} takenpakketten · {taken.filter((t) => !t.werkpakketId && !t.takenpakketId).length} losse taken</div>
          </div>
        </div>
        <div className="paneel kolom">
          <div className="rij wrap">
            <h2 className="groei">{geselecteerd ? geselecteerd.naam : 'Direct onder het project'}</h2>
            {geselecteerd?.werkwijze === 'AGILE' && agileAan && (
              <Link className="knop primair klein" to={`/projecten/${p.id}/agile/${geselecteerd.id}`}>
                Backlog, sprints en bord →
              </Link>
            )}
            {mag('pakketten.schrijven') && <button className="knop primair klein" onClick={() => setTpForm('nieuw')}>+ Takenpakket</button>}
            {mag('taken.aanmaken') && (
              <Link className="knop klein" to={`/taken/nieuw?project=${p.id}${geselecteerd ? `&werkpakket=${geselecteerd.id}` : ''}`} state={taak.state}>
                + Losse taak
              </Link>
            )}
          </div>
          <table className="tabel">
            <thead>
              <tr>
                <th>#</th>
                <th>Takenpakket</th>
                <th>Expertise</th>
                <th className="rechts">Uren</th>
                <th>Niveau</th>
                <th>{agile ? 'Opgepakt door' : 'Voortgang'}</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {tpsHier.map((t, i) => {
                const v = takenpakketVoortgang(t, tps, taken, geselecteerd?.deadline, d.vandaag);
                return (
                  <tr key={t.id}>
                    <td>{i + 1}</td>
                    <td>
                      <b>{t.naam}</b>
                      {t.afhankelijkVanId && <div className="klein mute">↪ na {tps.find((x) => x.id === t.afhankelijkVanId)?.naam}</div>}
                      {t.werkzaamheden && <div className="klein mute">{t.werkzaamheden}</div>}
                    </td>
                    <td>{t.expertise ?? '–'}</td>
                    <td className="rechts">{fmtUren(t.uren)}</td>
                    <td className="klein nowrap">{t.minNiveau ? `≥ ${t.minNiveau}%` : ''}{t.maxNiveau < 100 ? ` ≤ ${t.maxNiveau}%` : ''}</td>
                    {agile ? (
                      <>
                        <td className="klein">{d.engineers.find((e) => e.id === t.eigenaarId)?.naam ?? '–'}</td>
                        <td><Badge toon={ITEM_TOON[t.status ?? 'BACKLOG']}>{ITEM_STATUS_LABEL[t.status ?? 'BACKLOG']}</Badge></td>
                      </>
                    ) : (
                      <>
                        <td style={{ minWidth: 110 }}>
                          <div className="klein">{fmtUren(v.ingepland)} / {fmtUren(t.uren)}u</div>
                          <Balk waarde={v.ingepland} max={t.uren} />
                        </td>
                        <td><Badge toon={TOON[v.status]}>{v.label}</Badge></td>
                      </>
                    )}
                    <td className="nowrap">
                      {!agile && mag('taken.aanmaken') && v.status !== 'Ingepland' && (
                        <Link className="knop klein" to={`/taken/nieuw?project=${p.id}&takenpakket=${t.id}`} state={taak.state}>
                          + Taak inplannen
                        </Link>
                      )}
                      {mag('pakketten.schrijven') && (
                        <>
                          <button className="icoon-knop" onClick={() => setTpForm(t)} title="Bewerken"><Icoon naam="pencil" maat={16} /></button>
                          <VerwijderKnop klein label="🗑" onBevestig={() => verwijderTp.mutate(t.id)} />
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!tpsHier.length && <div className="leeg">Nog geen takenpakketten.</div>}
          {losseTaken.length > 0 && (
            <>
              <h3>Losse taken</h3>
              <TakenTabel taken={losseTaken} toonProject={false} />
            </>
          )}
          {geselecteerd && tpsHier.length > 0 && (
            <>
              <h3>Taken in dit werkpakket</h3>
              <TakenTabel taken={taken.filter((t) => tpsHier.some((x) => x.id === t.takenpakketId))} toonProject={false} />
            </>
          )}
        </div>
      </div>
      {wpForm && <WerkpakketForm projectId={p.id} wp={wpForm === 'nieuw' ? undefined : wpForm} alle={wps} onSluit={() => setWpForm(null)} />}
      {tpForm && <TakenpakketForm projectId={p.id} tp={tpForm === 'nieuw' ? undefined : tpForm} werkpakketId={geselecteerd?.id ?? null} onSluit={() => setTpForm(null)} />}
      {tplOpen && <TemplateKiezen projectId={p.id} onSluit={() => setTplOpen(false)} />}
      {expOpen && (
        <Modal titel="Uren per expertise" onSluit={() => setExpOpen(false)}>
          <table className="tabel">
            <thead>
              <tr><th>Expertise</th><th className="rechts">Budget</th><th className="rechts">In pakketten</th><th className="rechts">Nog vrij</th></tr>
            </thead>
            <tbody>
              {[...new Set([...p.expertiseBudget.map((b) => b.naam), ...tps.map((t) => t.expertise ?? '–')])].map((n) => {
                const b = p.expertiseBudget.find((x) => x.naam === n)?.uren ?? 0;
                const inp = tps.filter((t) => (t.expertise ?? '–') === n).reduce((s, t) => s + t.uren, 0);
                return (
                  <tr key={n}><td>{n}</td><td className="rechts">{fmtUren(b)}</td><td className="rechts">{fmtUren(inp)}</td><td className="rechts" style={{ color: b - inp < 0 ? 'var(--rood)' : undefined }}>{fmtUren(b - inp)}</td></tr>
                );
              })}
            </tbody>
          </table>
        </Modal>
      )}
    </div>
  );
}

function WerkpakketForm({ projectId, wp, alle, onSluit }: { projectId: string; wp?: WerkpakketRij; alle: WerkpakketRij[]; onSluit: () => void }) {
  const agileAan = useModule('AGILE');
  const [f, setF] = useState({
    naam: wp?.naam ?? '',
    deadline: wp?.deadline ?? '',
    afhankelijkVanId: wp?.afhankelijkVanId ?? '',
    omschrijving: wp?.omschrijving ?? '',
    velden: (wp?.velden ?? {}) as Record<string, unknown>,
    werkwijze: wp?.werkwijze ?? 'KLASSIEK',
    sprintWeken: wp?.sprintWeken ?? 2,
    focusfactor: wp?.focusfactor ?? 20,
    standup: wp?.standup ?? false,
    schatting: wp?.schatting ?? 'UREN',
    focusUren: wp?.focusUren ?? 0,
  });
  const opslaan = useActie(
    () => {
      const body = { projectId, naam: f.naam, deadline: f.deadline || null, afhankelijkVanId: f.afhankelijkVanId || null, omschrijving: f.omschrijving || null, velden: f.velden, volgorde: wp?.volgorde, werkwijze: f.werkwijze, sprintWeken: f.sprintWeken, focusfactor: f.focusfactor, standup: f.standup, schatting: f.schatting, focusUren: f.focusUren };
      return wp ? api.put(`/api/werkpakketten/${wp.id}`, body) : api.post('/api/werkpakketten', body);
    },
    { succes: 'Werkpakket opgeslagen', opNa: onSluit },
  );
  const verwijder = useActie(() => api.del(`/api/werkpakketten/${wp!.id}`), { succes: 'Werkpakket verwijderd', opNa: onSluit });
  return (
    <Modal
      titel={wp ? 'Werkpakket bewerken' : 'Nieuw werkpakket'}
      onSluit={onSluit}
      voet={
        <>
          {wp && <VerwijderKnop onBevestig={() => verwijder.mutate()} />}
          <div className="groei" />
          <button className="knop" onClick={onSluit}>Annuleren</button>
          <button className="knop primair" onClick={() => opslaan.mutate()}>Opslaan</button>
        </>
      }
    >
      <div className="grid2">
        <Veld label="Naam *" className="span2"><input value={f.naam} onChange={(e) => setF({ ...f, naam: e.target.value })} /></Veld>
        <Veld label="Deadline"><input type="date" value={f.deadline} onChange={(e) => setF({ ...f, deadline: e.target.value })} /></Veld>
        <Veld label="Start na werkpakket">
          <select value={f.afhankelijkVanId} onChange={(e) => setF({ ...f, afhankelijkVanId: e.target.value })}>
            <option value="">– geen –</option>
            {alle.filter((x) => x.id !== wp?.id).map((x) => <option key={x.id} value={x.id}>{x.naam}</option>)}
          </select>
        </Veld>
        <Veld label="Omschrijving" className="span2"><textarea value={f.omschrijving} onChange={(e) => setF({ ...f, omschrijving: e.target.value })} /></Veld>
        <div className="span2"><EigenVeldenInvoer entiteit="WERKPAKKET" waarden={f.velden} onChange={(v) => setF({ ...f, velden: v })} /></div>
        <Veld label="Werkwijze" hint={f.werkwijze === 'AGILE' ? 'Backlog, sprints en bord; engineers pakken zelf werk op' : 'Taken vooraf inplannen bij een engineer'}>
          <select value={f.werkwijze} onChange={(e) => setF({ ...f, werkwijze: e.target.value as 'KLASSIEK' | 'AGILE' })}>
            <option value="KLASSIEK">Klassiek (PRINCE2)</option>
            {(agileAan || f.werkwijze === 'AGILE') && <option value="AGILE">Agile (sprints){agileAan ? '' : ' – module uit'}</option>}
          </select>
        </Veld>
        {f.werkwijze === 'AGILE' && (
          <>
            <Veld label="Sprintlengte">
              <select value={f.sprintWeken} onChange={(e) => setF({ ...f, sprintWeken: Number(e.target.value) })}>
                {[1, 2, 3, 4].map((w) => <option key={w} value={w}>{w} {w === 1 ? 'week' : 'weken'}</option>)}
              </select>
            </Veld>
            <Veld label="Schatten in" hint={f.schatting === 'PUNTEN' ? 'Velocity, burndown en prognose in punten' : 'Velocity, burndown en prognose in uren'}>
              <select value={f.schatting} onChange={(e) => setF({ ...f, schatting: e.target.value as 'UREN' | 'PUNTEN' })}>
                <option value="UREN">Uren</option>
                <option value="PUNTEN">Punten</option>
              </select>
            </Veld>
            <Veld label="Focusfactor" hint="Ruimte voor overleg en onverwacht werk">
              <select value={f.focusfactor} onChange={(e) => setF({ ...f, focusfactor: Number(e.target.value) })}>
                {[...new Set([0, 10, 15, 20, 25, 30, 40, 50, f.focusfactor])].sort((a, b) => a - b).map((n) => <option key={n} value={n}>{n}%</option>)}
              </select>
            </Veld>
            <Veld label="Focusblok per dag" hint="Houdt de agenda van het team vrij voor sprintwerk">
              <select value={f.focusUren} onChange={(e) => setF({ ...f, focusUren: Number(e.target.value) })}>
                <option value={0}>Geen</option>
                {[1, 2, 3, 4].map((u) => <option key={u} value={u}>{u} uur (13:00–{13 + u}:00)</option>)}
              </select>
            </Veld>
            <Veld label="Stand-up" hint="Planning en review komen altijd in de agenda bij het starten van een sprint">
              <span className="check" style={{ minHeight: 34 }}>
                <input type="checkbox" checked={f.standup} onChange={(e) => setF({ ...f, standup: e.target.checked })} /> Dagelijks 09:00–09:15 in de agenda
              </span>
            </Veld>
          </>
        )}
      </div>
    </Modal>
  );
}

export function TakenpakketForm({ projectId, tp, werkpakketId, onSluit }: { projectId: string; tp?: TakenpakketRij; werkpakketId: string | null; onSluit: () => void }) {
  const { data: d } = useData();
  const [f, setF] = useState({
    naam: tp?.naam ?? '',
    werkpakketId: tp ? (tp.werkpakketId ?? '') : (werkpakketId ?? ''),
    expertise: tp?.expertise ?? '',
    uren: tp ? String(tp.uren) : '',
    dagen: tp ? String(tp.dagen) : '1',
    minNiveau: tp?.minNiveau ?? 0,
    maxNiveau: tp?.maxNiveau ?? 100,
    afhankelijkVanId: tp?.afhankelijkVanId ?? '',
    werkzaamheden: tp?.werkzaamheden ?? '',
    punten: tp?.punten != null ? String(tp.punten) : '',
  });
  const opslaan = useActie(
    () => {
      const body = { projectId, naam: f.naam, werkpakketId: f.werkpakketId || null, expertise: f.expertise || null, uren: getal(f.uren) ?? 0, dagen: getal(f.dagen) ?? 1, minNiveau: f.minNiveau, maxNiveau: f.maxNiveau, afhankelijkVanId: f.afhankelijkVanId || null, werkzaamheden: f.werkzaamheden || null, volgorde: tp?.volgorde, punten: getal(f.punten) ?? null };
      return tp ? api.put(`/api/takenpakketten/${tp.id}`, body) : api.post('/api/takenpakketten', body);
    },
    { succes: 'Takenpakket opgeslagen', opNa: onSluit },
  );
  if (!d) return null;
  const p = d.projecten.find((x) => x.id === projectId)!;
  const budgetExp = p.expertiseBudget.find((b) => b.naam === f.expertise)?.uren ?? 0;
  const alInPakketten = d.takenpakketten.filter((t) => t.projectId === projectId && t.expertise === f.expertise && t.id !== tp?.id).reduce((s, t) => s + t.uren, 0);
  return (
    <Modal
      titel={tp ? 'Takenpakket bewerken' : 'Nieuw takenpakket'}
      onSluit={onSluit}
      voet={<><button className="knop" onClick={onSluit}>Annuleren</button><button className="knop primair" onClick={() => opslaan.mutate()}>Opslaan</button></>}
    >
      <div className="grid2">
        <Veld label="Naam *" className="span2"><input value={f.naam} onChange={(e) => setF({ ...f, naam: e.target.value })} /></Veld>
        <Veld label="Werkpakket">
          <select value={f.werkpakketId} onChange={(e) => setF({ ...f, werkpakketId: e.target.value })}>
            <option value="">Direct onder het project</option>
            {d.werkpakketten.filter((w) => w.projectId === projectId).map((w) => <option key={w.id} value={w.id}>{w.naam}</option>)}
          </select>
        </Veld>
        <Veld label="Expertise" hint={f.expertise ? `Nog vrij van deze expertise: ${fmtUren(budgetExp - alInPakketten)}u (budget ${fmtUren(budgetExp)}u)` : undefined}>
          <select value={f.expertise} onChange={(e) => setF({ ...f, expertise: e.target.value })}>
            <option value="">– geen –</option>
            {d.expertises.map((x) => <option key={x.id} value={x.naam}>{x.naam}</option>)}
          </select>
        </Veld>
        <Veld label="Uren *"><input value={f.uren} onChange={(e) => setF({ ...f, uren: e.target.value })} /></Veld>
        {d.werkpakketten.find((w) => w.id === f.werkpakketId)?.schatting === 'PUNTEN' && (
          <Veld label="Punten" hint="Schatting voor velocity en burndown; uren blijven voor budget en capaciteit">
            <select value={f.punten} onChange={(e) => setF({ ...f, punten: e.target.value })}>
              <option value="">–</option>
              {[1, 2, 3, 5, 8, 13, 20, 40].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </Veld>
        )}
        <Veld label="Aantal dagen (indicatie)"><input value={f.dagen} onChange={(e) => setF({ ...f, dagen: e.target.value })} /></Veld>
        <Veld label="Minimaal kennisniveau">
          <select value={f.minNiveau} onChange={(e) => setF({ ...f, minNiveau: Number(e.target.value) })}>
            <option value={0}>Geen minimum</option>
            {NIVEAUS.map((n) => <option key={n.p} value={n.p}>{n.label}</option>)}
          </select>
        </Veld>
        <Veld label="Maximaal kennisniveau" hint="Bijv. 50% om specialisten vrij te houden">
          <select value={f.maxNiveau} onChange={(e) => setF({ ...f, maxNiveau: Number(e.target.value) })}>
            <option value={100}>Geen maximum</option>
            {NIVEAUS.filter((n) => n.p < 100).map((n) => <option key={n.p} value={n.p}>{n.label}</option>)}
          </select>
        </Veld>
        <Veld label="Start na takenpakket" className="span2">
          <select value={f.afhankelijkVanId} onChange={(e) => setF({ ...f, afhankelijkVanId: e.target.value })}>
            <option value="">– geen –</option>
            {d.takenpakketten.filter((x) => x.projectId === projectId && x.id !== tp?.id).map((x) => <option key={x.id} value={x.id}>{x.naam}</option>)}
          </select>
        </Veld>
        <Veld label="Werkzaamheden" className="span2"><textarea value={f.werkzaamheden} onChange={(e) => setF({ ...f, werkzaamheden: e.target.value })} /></Veld>
      </div>
    </Modal>
  );
}

function TemplateKiezen({ projectId, onSluit }: { projectId: string; onSluit: () => void }) {
  const { data: d } = useData();
  const [gekozen, setGekozen] = useState<string[]>([]);
  const toepassen = useActie(() => api.post(`/api/projecten/${projectId}/template`, { templateIds: gekozen }), { succes: 'Template toegepast', opNa: onSluit });
  if (!d) return null;
  const wps = d.templates.filter((t) => t.soort === 'WERKPAKKET').sort((a, b) => a.volgorde - b.volgorde);
  return (
    <Modal titel="Uit template" onSluit={onSluit} voet={<><button className="knop" onClick={onSluit}>Annuleren</button><button className="knop primair" disabled={!gekozen.length} onClick={() => toepassen.mutate()}>Toepassen</button></>}>
      <p className="mute klein">Kies één of meer werkpakket-templates. De deadlines worden berekend vanaf de livegang van het project.</p>
      {wps.map((t) => (
        <label key={t.id} className="paneel vlak check" style={{ display: 'flex', marginBottom: 6 }}>
          <input type="checkbox" checked={gekozen.includes(t.id)} onChange={(e) => setGekozen(e.target.checked ? [...gekozen, t.id] : gekozen.filter((x) => x !== t.id))} />
          <div>
            <b>{t.naam}</b>
            <div className="klein mute">
              {t.dagenTovLivegang != null ? `${Math.abs(t.dagenTovLivegang)} dagen ${t.dagenTovLivegang < 0 ? 'voor' : 'na'} livegang` : 'geen deadline'} ·{' '}
              {d.templates.filter((x) => x.ouderId === t.id).map((x) => x.naam).join(', ') || 'geen takenpakketten'}
            </div>
          </div>
        </label>
      ))}
      {!wps.length && <div className="leeg">Nog geen templates. Maak ze bij Beheer › Templates.</div>}
    </Modal>
  );
}
